import PageHero from "@/components/PageHero";
import SectionLabel from "@/components/SectionLabel";
import EditorialBanner from "@/components/EditorialBanner";
import { ArrowUpRight, FileText, Newspaper, BookOpen, Mic } from "lucide-react";

const RES = [
  {
    type: "Whitepaper",
    icon: FileText,
    title: "The Compliance Intelligence Overlay",
    desc: "An architectural primer on how Sentinel sits above your KYC / AML / TM / Fraud stack — and why deterministic beats black-box at AMLA scale.",
    meta: "PDF",
  },
  {
    type: "Regulatory Brief",
    icon: BookOpen,
    title: "Reading AMLA Like an Engineer",
    desc: "The articles, RTS and ITS that actually change how you build a control.",
    meta: "Brief",
  },
  {
    type: "Whitepaper",
    icon: FileText,
    title: "Missed Risk Beyond BaaS",
    desc: "Four patterns where missed risk lives inside a single bank — multi-CIF universal estates, M&A duplicates, cross-jurisdictional subsidiaries and UBO networks.",
    meta: "PDF",
  },
  {
    type: "Regulatory Brief",
    icon: BookOpen,
    title: "EU AI Act Article 14 in Practice",
    desc: "What 'effective human oversight' looks like in a compliance product — and why advisory-only isn't just a label.",
    meta: "Brief",
  },
  {
    type: "Field Note",
    icon: Newspaper,
    title: "Cutting Analyst Time on AML & Fraud FPs",
    desc: "Anonymised walk-through: 30 minutes to 5 minutes, with rationale a regulator can read.",
    meta: "Field note",
  },
  {
    type: "Podcast",
    icon: Mic,
    title: "Hawk-eye Compliance — Episode 01",
    desc: "Our founding team on what we learned auditing fragmented compliance estates.",
    meta: "Podcast",
  },
];

export default function Resources() {
  return (
    <div data-testid="resources-page">
      <PageHero
        eyebrow="Resources"
        title="What we learn solving those two problems, we publish."
        sub="Whitepapers, regulatory briefs and field notes on false-positive reduction and missed-risk surfacing &mdash; from the front line of EU compliance. No gated demos disguised as content."
        breadcrumb={[{ label: "Lexifide.ai", to: "/" }, { label: "Resources" }]}
        testId="resources-hero"
      />

      <section className="light-section py-24 lg:py-32">
        <div className="max-w-[1380px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
            <div>
              <SectionLabel index="01" label="Library" />
              <h2 className="mt-6 font-display font-bold text-[#090a0f] text-4xl sm:text-5xl lg:text-[52px] tracking-tight leading-[1.02]">
                Six pieces.<br />
                <span className="text-[#52525b]">No marketing waffle.</span>
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {["All", "Whitepaper", "Regulatory Brief", "Podcast", "Field Note"].map((c, i) => (
                <button
                  key={c}
                  data-testid={`resources-filter-${i}`}
                  className={
                    i === 0
                      ? "px-3.5 py-2 font-mono text-[11px] tracking-[0.16em] uppercase bg-[#090a0f] text-white"
                      : "px-3.5 py-2 font-mono text-[11px] tracking-[0.16em] uppercase border border-[#e4e4e7] text-[#52525b] hover:border-[#090a0f] hover:text-[#090a0f] transition-colors"
                  }
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <EditorialBanner
            variant="light"
            gradient="paper-violet"
            image="https://images.unsplash.com/photo-1457369804613-52c61a468e7d?auto=format&fit=crop&w=2400&q=80"
            kicker="The library"
            headline="What we learn solving false positives and missed risk — we publish."
            chips={[
              { c: "#0047ff", t: "Whitepapers" },
              { c: "#7c3aed", t: "Regulatory briefs" },
              { c: "#f59e0b", t: "Field notes" },
            ]}
            testId="resources-banner"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
            {RES.map((r, i) => {
              const Icon = r.icon;
              const TYPE_ACCENT = {
                "Whitepaper": { hex: "#0047ff", soft: "rgba(0,71,255,0.08)" },
                "Regulatory Brief": { hex: "#7c3aed", soft: "rgba(124,58,237,0.10)" },
                "Field Note": { hex: "#f59e0b", soft: "rgba(245,158,11,0.12)" },
                "Podcast": { hex: "#f43f5e", soft: "rgba(244,63,94,0.10)" },
              };
              const a = TYPE_ACCENT[r.type] || { hex: "#0047ff", soft: "rgba(0,71,255,0.08)" };
              return (
                <a
                  key={r.title}
                  href="/get-started?type=general"
                  data-testid={`resource-card-${i}`}
                  className="bg-white p-7 lg:p-8 flex flex-col gap-5 min-h-[300px] group hover:bg-[#0d0e16] hover:text-white transition-colors duration-300 relative"
                >
                  <span
                    className="absolute top-0 left-0 right-0 h-[3px]"
                    style={{ backgroundColor: a.hex }}
                  />
                  <div className="flex items-center justify-between">
                    <span
                      className="inline-flex items-center gap-2 px-2 py-1 font-mono text-[10px] tracking-[0.22em] uppercase"
                      style={{ color: a.hex, backgroundColor: a.soft }}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {r.type}
                    </span>
                    <ArrowUpRight className="w-4 h-4 text-[#a1a1aa] group-hover:text-white transition-colors" />
                  </div>
                  <div className="mt-auto">
                    <p className="font-display font-bold text-[20px] tracking-tight leading-tight">
                      {r.title}
                    </p>
                    <p className="mt-3 text-[14px] leading-relaxed text-[#52525b] group-hover:text-white/70 transition-colors">
                      {r.desc}
                    </p>
                    <p className="mt-5 font-mono text-[11px] tracking-[0.16em] uppercase text-[#a1a1aa] group-hover:text-white/45 transition-colors">
                      {r.meta}
                    </p>
                  </div>
                </a>
              );
            })}
          </div>

          <div className="mt-10 p-8 border border-[#e4e4e7] bg-white flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div>
              <p className="font-display font-bold text-[#090a0f] text-[22px] tracking-tight">
                Subscribe to the Lexifide.ai brief.
              </p>
              <p className="mt-1 text-[#52525b] text-[14px]">
                One short note when something in AMLA, the EU AI Act or DORA actually moves.
              </p>
            </div>
            <a
              href="/get-started?type=subscribe"
              data-testid="resources-subscribe-cta"
              className="btn-primary-light"
            >
              Subscribe via email <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
